import Papa from 'papaparse';
import { splitPhoneValue } from './phoneUtils';

export const FRESHA_CUSTOMER_COLUMNS = {
  CLIENT_ID: 0,
  FIRST_NAME: 1,
  LAST_NAME: 2,
  FULL_NAME: 3,
  GENDER: 6,
  MOBILE: 7,
  PHONE: 8,
  EMAIL: 9,
  BIRTH_DATE: 18,
  NOTE: 20,
};

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const GENDER_MAP = {
  masculino: 1,
  male: 1,
  m: 1,
  feminino: 2,
  female: 2,
  f: 2,
};

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((line) => line.trim()) || '');
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
};

const normalizeWhitespace = (value) => (value == null ? '' : String(value).trim());

export const buildCustomerName = (firstName, lastName, fullName) => {
  const full = normalizeWhitespace(fullName);
  if (full) return full;
  return [normalizeWhitespace(firstName), normalizeWhitespace(lastName)].filter(Boolean).join(' ');
};

const toInternationalPhoneRaw = (raw) => {
  if (raw.includes('+')) {
    return raw;
  }

  const digits = raw.replace(/[^\d]/g, '');
  if (digits.startsWith('00') && digits.length > 8) {
    return `+${digits.slice(2)}`;
  }

  return raw;
};

const phoneFromCountrySplit = (raw) => {
  const split = splitPhoneValue(toInternationalPhoneRaw(raw));
  const nationalDigits = split.number.replace(/[^\d]/g, '');
  if (nationalDigits.length < 6 || !split.countryCode?.startsWith('+')) {
    return null;
  }

  return {
    countryCode: split.countryCode,
    number: nationalDigits,
  };
};

export const parseFreshaPhone = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { phone: null, warningKey: null, original: '' };
  }

  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length < 8) {
    return { phone: null, warningKey: 'dataImport.customers.warnings.invalidPhone', original: raw };
  }

  const hasExplicitCountry = raw.includes('+') || /^\s*00/.test(raw);
  if (hasExplicitCountry) {
    const phone = phoneFromCountrySplit(raw);
    if (phone) {
      return { phone, warningKey: null, original: raw };
    }
  }

  let countryDigits;
  let nationalDigits;

  if (digits.startsWith('351') && digits.length >= 12) {
    countryDigits = '351';
    nationalDigits = digits.slice(3);
  } else if (digits.startsWith('34') && digits.length >= 11) {
    countryDigits = '34';
    nationalDigits = digits.slice(2);
  } else if (digits.startsWith('33') && digits.length >= 11) {
    countryDigits = '33';
    nationalDigits = digits.slice(2);
  } else if (digits.startsWith('55') && digits.length >= 12) {
    countryDigits = '55';
    nationalDigits = digits.slice(2);
  } else if (digits.length >= 10) {
    countryDigits = digits.slice(0, digits.length - 9);
    nationalDigits = digits.slice(-9);
    if (!countryDigits) {
      return { phone: null, warningKey: 'dataImport.customers.warnings.invalidPhone', original: raw };
    }
  } else {
    return { phone: null, warningKey: 'dataImport.customers.warnings.invalidPhone', original: raw };
  }

  return {
    phone: {
      countryCode: `+${countryDigits}`,
      number: nationalDigits,
    },
    warningKey: null,
    original: raw,
  };
};

export const parseFreshaGenre = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { genre: null, warningKey: null, original: '' };
  }

  const genre = GENDER_MAP[raw.toLowerCase()];
  if (!genre) {
    return { genre: null, warningKey: 'dataImport.customers.warnings.invalidGenre', original: raw };
  }

  return { genre, warningKey: null, original: raw };
};

export const parseFreshaBirthDate = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { birthDate: null, warningKey: null, original: '' };
  }

  const match = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (!match) {
    return { birthDate: null, warningKey: 'dataImport.customers.warnings.invalidBirthDate', original: raw };
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) {
    year += year >= 50 ? 1900 : 2000;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return { birthDate: null, warningKey: 'dataImport.customers.warnings.invalidBirthDate', original: raw };
  }

  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return { birthDate: iso, warningKey: null, original: raw };
};

const mapRow = (cells, rowIndex, headers) => {
  const freshaClientId = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.CLIENT_ID]);
  const firstName = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.FIRST_NAME]);
  const lastName = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.LAST_NAME]);
  const fullName = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.FULL_NAME]);
  const email = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.EMAIL]);
  const mobileRaw = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.MOBILE]);
  const phoneRaw = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.PHONE]);
  const genderRaw = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.GENDER]);
  const birthDateRaw = normalizeWhitespace(cells[FRESHA_CUSTOMER_COLUMNS.BIRTH_DATE]);

  const name = buildCustomerName(firstName, lastName, fullName);
  const phoneParse = parseFreshaPhone(mobileRaw || phoneRaw);
  const genreParse = parseFreshaGenre(genderRaw);
  const birthDateParse = parseFreshaBirthDate(birthDateRaw);

  const errors = [];
  if (!name) {
    errors.push({ field: 'name', key: 'dataImport.customers.errors.nameRequired', original: fullName || firstName });
  }
  const hasPhone = Boolean(phoneParse.phone?.countryCode && phoneParse.phone?.number);
  if (email) {
    if (!EMAIL_REGEX.test(email)) {
      errors.push({ field: 'email', key: 'dataImport.customers.errors.emailInvalid', original: email });
    }
  } else if (!hasPhone) {
    errors.push({ field: 'contact', key: 'dataImport.customers.errors.contactRequired', original: mobileRaw || phoneRaw || email });
  }

  const warnings = [];
  if (phoneParse.warningKey) {
    warnings.push({ field: 'phone', key: phoneParse.warningKey, original: phoneParse.original });
  }
  if (genreParse.warningKey) {
    warnings.push({ field: 'genre', key: genreParse.warningKey, original: genreParse.original });
  }
  if (birthDateParse.warningKey) {
    warnings.push({ field: 'birthDate', key: birthDateParse.warningKey, original: birthDateParse.original });
  }

  const rawCells = headers.map((header, index) => ({
    header: header || `Column ${index + 1}`,
    value: normalizeWhitespace(cells[index]),
  }));

  return {
    id: `${rowIndex}-${freshaClientId || 'row'}`,
    rowNumber: rowIndex + 1,
    freshaClientId,
    name,
    email,
    phone: phoneParse.phone,
    genre: genreParse.genre,
    birthDate: birthDateParse.birthDate,
    rawCells,
    errors,
    warnings,
    original: {
      name: fullName || [firstName, lastName].filter(Boolean).join(' '),
      email,
      phone: mobileRaw || phoneRaw,
      genre: genderRaw,
      birthDate: birthDateRaw,
    },
  };
};

export const parseFreshaCustomerCsv = (fileText) => {
  const delimiter = detectDelimiter(fileText);
  const parsed = Papa.parse(fileText, {
    delimiter,
    skipEmptyLines: 'greedy',
  });

  if (!parsed.data?.length) {
    return { rows: [], headers: [], delimiter, errorKey: 'dataImport.customers.errors.emptyFile' };
  }

  const [headerRow, ...dataRows] = parsed.data;
  const headers = (headerRow || []).map((value) => normalizeWhitespace(value));
  const rows = dataRows
    .filter((cells) => Array.isArray(cells) && cells.some((cell) => normalizeWhitespace(cell)))
    .map((cells, index) => mapRow(cells, index + 1, headers));

  return { rows, headers, delimiter, errorKey: null };
};

export const buildFailingCustomersCsv = (failedRows, headers = [], delimiter = ';') => {
  const columnCount = Math.max(
    headers.length,
    ...failedRows.map((row) => row.rawCells?.length || 0),
  );
  const csvHeaders = Array.from({ length: columnCount }, (_, index) => (
    headers[index]
    || failedRows[0]?.rawCells?.[index]?.header
    || `Column ${index + 1}`
  ));

  const data = failedRows.map((row) => (
    csvHeaders.map((_, index) => row.rawCells?.[index]?.value ?? '')
  ));

  return Papa.unparse(
    { fields: csvHeaders, data },
    { delimiter, newline: '\r\n' },
  );
};

export const revalidateCustomerRow = (row) => {
  const errors = [];
  const name = normalizeWhitespace(row.name);
  const email = normalizeWhitespace(row.email);
  const hasPhone = Boolean(row.phone?.countryCode && row.phone?.number);

  if (!name) {
    errors.push({ field: 'name', key: 'dataImport.customers.errors.nameRequired', original: row.original?.name || '' });
  }
  if (email) {
    if (!EMAIL_REGEX.test(email)) {
      errors.push({ field: 'email', key: 'dataImport.customers.errors.emailInvalid', original: row.original?.email || email });
    }
  } else if (!hasPhone) {
    errors.push({ field: 'contact', key: 'dataImport.customers.errors.contactRequired', original: row.original?.email || row.original?.phone || '' });
  }

  const warnings = (row.warnings || []).filter((warning) => {
    if (warning.field === 'phone' && row.phone?.countryCode && row.phone?.number) return false;
    if (warning.field === 'genre' && row.genre) return false;
    if (warning.field === 'birthDate' && row.birthDate) return false;
    return true;
  });

  return {
    ...row,
    name,
    email,
    errors,
    warnings,
  };
};

const normalizeKey = (value) => normalizeWhitespace(value).toLowerCase();

const phoneKey = (phone) => {
  if (!phone?.countryCode || !phone?.number) return '';
  return `${phone.countryCode}${phone.number}`.replace(/[^\d]/g, '');
};

const memberSetKey = (members) => members.map((row) => row.id).sort().join('|');

export const duplicateGroupIncludesEmail = (group) =>
  Boolean(group?.types?.includes('email') || String(group?.type || '').split(/[+_]/).includes('email'));

export const buildInitialDuplicateDecisions = (groups) => {
  const decisions = {};
  (groups || []).forEach((group) => {
    decisions[group.id] = {
      action: '',
      memberIds: group.rows.map((row) => row.id),
      primaryRowId: group.rows[0]?.id || null,
    };
  });
  return decisions;
};

export const findCustomerDuplicateGroups = (rows) => {
  const readyRows = rows.filter((row) => row.errors.length === 0);
  const groupsByMembers = new Map();

  const collectBy = (getKey, type) => {
    const buckets = new Map();
    readyRows.forEach((row) => {
      const key = getKey(row);
      if (!key) return;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(row);
    });

    buckets.forEach((members, key) => {
      if (members.length < 2) return;
      const setKey = memberSetKey(members);
      const existing = groupsByMembers.get(setKey);
      if (existing) {
        if (!existing.types.includes(type)) {
          existing.types.push(type);
          existing.keys[type] = key;
        }
        return;
      }
      groupsByMembers.set(setKey, {
        types: [type],
        keys: { [type]: key },
        rows: members,
      });
    });
  };

  collectBy((row) => normalizeKey(row.email), 'email');
  collectBy((row) => normalizeKey(row.name), 'name');
  collectBy((row) => phoneKey(row.phone), 'phone');

  return [...groupsByMembers.values()].map((group) => {
    const types = [...group.types].sort();
    const type = types.join('_');
    const key = types.map((item) => group.keys[item]).join(' · ');
    return {
      id: `${type}:${key}`,
      type,
      key,
      types,
      rows: group.rows,
    };
  });
};

const collectDuplicateBuckets = (rows, getKey) => {
  const buckets = new Map();
  rows.forEach((row) => {
    const key = getKey(row);
    if (!key) return;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  });
  return [...buckets.entries()]
    .map(([key, members]) => ({ key, members }))
    .filter(({ members }) => members.length > 1);
};

export const buildAutomaticMergeDecisions = (rows) => {
  const readyRows = rows.filter((row) => row.errors.length === 0);
  const decisions = {};
  const claimed = new Set();

  const addMergeDecision = (type, key, members) => {
    const sorted = [...members].sort((a, b) => a.rowNumber - b.rowNumber);
    const memberIds = sorted.map((row) => row.id);
    decisions[`${type}:${key}`] = {
      action: 'merge',
      memberIds,
      primaryRowId: sorted[0].id,
    };
    memberIds.forEach((id) => claimed.add(id));
  };

  collectDuplicateBuckets(readyRows, (row) => normalizeKey(row.email))
    .forEach(({ key, members }) => addMergeDecision('email', key, members));

  const unclaimedRows = readyRows.filter((row) => !claimed.has(row.id));
  collectDuplicateBuckets(unclaimedRows, (row) => {
    const name = normalizeKey(row.name);
    const phone = phoneKey(row.phone);
    if (!name || !phone) return '';
    return `${name}|${phone}`;
  }).forEach(({ key, members }) => addMergeDecision('namePhone', key, members));

  return decisions;
};

export const buildImportCustomersPayload = (rows, mergeDecisions) => {
  const readyRows = rows.filter((row) => row.errors.length === 0);
  const rowById = new Map(readyRows.map((row) => [row.id, row]));
  const consumed = new Set();
  const customers = [];

  Object.entries(mergeDecisions || {}).forEach(([groupId, decision]) => {
    if (decision?.action !== 'merge' || !decision.primaryRowId) return;
    const primary = rowById.get(decision.primaryRowId);
    if (!primary) return;

    const memberIds = decision.memberIds || [];
    const aliases = memberIds
      .filter((id) => id !== primary.id)
      .map((id) => rowById.get(id)?.freshaClientId)
      .filter(Boolean);

    memberIds.forEach((id) => consumed.add(id));
    customers.push({
      freshaClientId: primary.freshaClientId,
      name: primary.name,
      email: primary.email || null,
      phone: primary.phone || null,
      genre: primary.genre || null,
      birthDate: primary.birthDate || null,
      aliasFreshaClientIds: aliases,
    });
  });

  readyRows.forEach((row) => {
    if (consumed.has(row.id)) return;
    customers.push({
      freshaClientId: row.freshaClientId,
      name: row.name,
      email: row.email || null,
      phone: row.phone || null,
      genre: row.genre || null,
      birthDate: row.birthDate || null,
      aliasFreshaClientIds: [],
    });
  });

  return { customers };
};
